import { isMatch, isObject, partialRight } from 'lodash';
import { createSelector } from 'reselect';
import type { EntityId, EntityState } from './EntityState';

/**
 * A filter config object, used to query for entities with matching attributes.
 */
export type EntityAttributesFilter<T> = {
  [key in keyof T]?: Partial<T[key]>;
};

/**
 * A set of selectors for querying an entity's normalized state.
 */
export interface EntityStateSelectors<T, V, Id extends EntityId> {
  /** Returns the entity IDs */
  selectIds: (state: V) => Id[];
  /** Returns the map of entities by ID */
  selectEntities: (state: V) => Record<Id, T>;
  /** Returns all entities */
  selectAll: (state: V) => T[];
  /** Returns total count of entities */
  selectTotal: (state: V) => number;
  /** Returns a collection of entities, filtered by the provided attributes */
  selectByAttributes: (state: V, attributes: EntityAttributesFilter<T>) => T[];
  /** Returns the entity with the matching ID if it exists */
  selectById: (state: V, id: Id) => T | undefined;
}

/**
 * A factory for creating {@link EntityStateSelectors}
 */
export interface EntityStateSelectorsFactory<T, Id extends EntityId> {
  createSelectors: <V>(
    selectState: (state: V) => EntityState<T, Id>,
  ) => EntityStateSelectors<T, V, Id>;
  createSelectIds: <V>(
    selectState: (state: V) => EntityState<T, Id>,
  ) => EntityStateSelectors<T, V, Id>['selectIds'];
  createSelectEntities: <V>(
    selectState: (state: V) => EntityState<T, Id>,
  ) => EntityStateSelectors<T, V, Id>['selectEntities'];
  createSelectAll: <V>(
    selectState: (state: V) => EntityState<T, Id>,
  ) => EntityStateSelectors<T, V, Id>['selectAll'];
  createSelectTotal: <V>(
    selectState: (state: V) => EntityState<T, Id>,
  ) => EntityStateSelectors<T, V, Id>['selectTotal'];
  createSelectByAttributes: <V>(
    selectState: (state: V) => EntityState<T, Id>,
    attributes: EntityAttributesFilter<T>,
  ) => (state: V) => ReturnType<EntityStateSelectors<T, V, Id>['selectByAttributes']>;
  createSelectById: <V>(
    selectState: (state: V) => EntityState<T, Id>,
    id: Id,
  ) => (state: V) => ReturnType<EntityStateSelectors<T, V, Id>['selectById']>;
}

/**
 * Creates an instance of an {@link EntityStateSelectorsFactory}, used to create
 * a set of selectors for querying an entity's normalized state.
 */
export const createEntityStateSelectorsFactory = <
  T,
  Id extends EntityId,
>(): EntityStateSelectorsFactory<T, Id> => {
  const createSelectors = <V>(selectState: (state: V) => EntityState<T, Id>) => {
    const selectIds = (state: V) => selectState(state).ids;

    const selectEntities = (state: V) => selectState(state).entities;

    const selectAll = createSelector(selectIds, selectEntities, (ids, entities) =>
      ids.map(id => entities[id]).filter(Boolean),
    );

    const selectTotal = createSelector(selectIds, ids => ids.length);

    const selectById = createSelector(
      selectEntities,
      (_: unknown, id: Id) => id,
      (entities, id) => entities[id],
    );

    const selectByAttributes = createSelector(
      selectAll,
      (_: unknown, attributes: EntityAttributesFilter<T>) => attributes,
      (all, attributes) =>
        all.filter(entity => {
          if (!entity || !isObject(entity)) {
            return false;
          }

          if (!attributes || Object.keys(attributes).length === 0) {
            return true;
          }

          // NB: we want to match on partials so that given an entity with multiple attributes,
          // a user can filter against one known attribute, like this:
          //   `selectByAttributes(state, { someAttr: 'someValue' })`
          // Without the isMatch() util, the entire object would need to match, making querying unnecessarily difficult.
          return isMatch(entity, attributes);
        }),
    );

    return {
      selectAll,
      selectEntities,
      selectIds,
      selectTotal,
      selectById,
      selectByAttributes,
    };
  };

  const createSelectIds = <V>(selectState: (state: V) => EntityState<T, Id>) =>
    createSelectors(selectState).selectIds;
  const createSelectEntities = <V>(selectState: (state: V) => EntityState<T, Id>) =>
    createSelectors(selectState).selectEntities;
  const createSelectAll = <V>(selectState: (state: V) => EntityState<T, Id>) =>
    createSelectors(selectState).selectAll;
  const createSelectTotal = <V>(selectState: (state: V) => EntityState<T, Id>) =>
    createSelectors(selectState).selectTotal;
  const createSelectByAttributes = <V>(
    selectState: (state: V) => EntityState<T, Id>,
    attributes: EntityAttributesFilter<T>,
  ) => partialRight(createSelectors(selectState).selectByAttributes, attributes);
  const createSelectById = <V>(selectState: (state: V) => EntityState<T, Id>, id: Id) =>
    partialRight(createSelectors(selectState).selectById, id);

  return {
    createSelectors,
    createSelectIds,
    createSelectEntities,
    createSelectAll,
    createSelectTotal,
    createSelectByAttributes,
    createSelectById,
  };
};
